import { Controller, useWatch, type Control, type UseFormSetValue, type Path } from 'react-hook-form';
import {
  Box,
  TextField,
  Stack,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import { type StepsState } from '../add-edit-contributor.schema';
import { type ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { useState, useRef, useEffect, useMemo, type ChangeEvent } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EastIcon from '@mui/icons-material/East';
import ProfileImagePreview from '@/components/user-dashboard/contributions/profile/profile-image-preview';
import { useNotifications } from '@/hooks/use-notifications';
import { useUploadEventContributorProfilePhotoMutation } from '@/state/mutations/contributors/use-upload-event-contributor-profile-photo';
import { useMarkProfileReviewedMutation } from '@/state/mutations/contributors/use-mark-profile-reviewed';
import { useQueryClient } from '@tanstack/react-query';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { useEventStore } from '@/state/stores/use-event-store';
import { ProfilePhotoAvatar } from '@/components/common/profile-photo-avatar';
import {
  useGetContributorProfilePhotoUrl,
  GET_CONTRIBUTOR_PROFILE_PHOTO_URL_QUERY_KEY,
} from '@/state/queries/contributors/use-get-contributor-profile-photo-url';
import { getContributorProfileReviewIconColor } from '@/components/events/detail/tabs/sessions/session-actors/speaker-profile-review-status';
import { DiffText } from '@/components/common/diff-text';

interface GeneralFormProps {
  control: Control<StepsState>;
  contributor?: ContributorDto;
  setValue: UseFormSetValue<StepsState>;
  onMarkReviewed?: () => void;
}

export default function GeneralForm({ control, contributor, setValue, onMarkReviewed }: GeneralFormProps) {
  const t = useTranslations();
  const notification = useNotifications();
  const queryClient = useQueryClient();
  const eventId = useEventStore((s) => s.event?.id);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isContributorPhotoPreviewOpen, setIsContributorPhotoPreviewOpen] = useState(false);
  const [isEventPhotoPreviewOpen, setIsEventPhotoPreviewOpen] = useState(false);
  const [uploadedAssetName, setUploadedAssetName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutateAsync: uploadProfilePhotoMutation } = useUploadEventContributorProfilePhotoMutation();
  const { mutateAsync: markProfileReviewedMutation, isPending: isMarkingReviewed } = useMarkProfileReviewedMutation();
  const [markedReviewedLocally, setMarkedReviewedLocally] = useState(false);

  const serverReviewStatus = useMemo(
    () => (contributor ? getContributorProfileReviewIconColor(contributor) : 'default'),
    [contributor],
  );
  const profileReviewStatus = markedReviewedLocally ? 'success' : serverReviewStatus;

  // Reset the local override once the server state catches up
  useEffect(() => {
    if (markedReviewedLocally && serverReviewStatus === 'success') {
      setMarkedReviewedLocally(false);
    }
  }, [markedReviewedLocally, serverReviewStatus]);

  const contributorProfile = contributor?.userContributor?.providedProfile;
  const eventProfile = contributor?.profiles?.[0];
  const hasContributorProfile = !!contributorProfile;
  const contributorId = contributor?.id;

  const contributorPhotoUri = contributorProfile?.photoUri || '';
  const eventAssetName = uploadedAssetName || eventProfile?.assetName || undefined;

  const { data: eventPhotoUrl } = useGetContributorProfilePhotoUrl(contributorId, eventAssetName);

  useEffect(() => {
    if (uploadedAssetName && eventProfile?.assetName === uploadedAssetName) {
      setUploadedAssetName(null);
    }
  }, [eventProfile?.assetName, uploadedAssetName]);

  useEffect(() => {
    if (contributorId && eventProfile?.assetName) {
      queryClient.invalidateQueries({
        queryKey: [GET_CONTRIBUTOR_PROFILE_PHOTO_URL_QUERY_KEY, contributorId, eventProfile.assetName],
      });
    }
  }, [contributorId, eventProfile?.assetName, queryClient]);

  const showComparisonView = !!contributor;
  type ViewMode = 'contributor' | 'comparison' | 'event';
  const [viewMode, setViewMode] = useState<ViewMode>('comparison');
  const showContributorPanel = viewMode === 'contributor' || viewMode === 'comparison';
  const showEventPanel = viewMode === 'event' || viewMode === 'comparison';

  const handleMarkAsReviewed = async () => {
    if (!contributorId) return;
    try {
      await markProfileReviewedMutation(contributorId);
      setMarkedReviewedLocally(true);
      onMarkReviewed?.();
      if (eventId) {
        await queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY, eventId] });
      }
      notification.notifySuccess(t('event.detail.addEditContributor.profileMarkedAsReviewed'));
    } catch {
      notification.notifyError(t('event.detail.addEditContributor.profileMarkedAsReviewedError'));
    }
  };

  const handleCopyAll = () => {
    if (!contributorProfile) return;
    setValue('general.fullName', contributorProfile.displayName || '');
    setValue('general.title', contributorProfile.title || '');
    setValue('general.organization', contributorProfile.organization || '');
    setValue('general.biography', contributorProfile.bio || '');
    setValue('general.email', contributorProfile.contactEmail || contributor?.email || '');
    setValue('general.phoneNumber', contributorProfile.contactPhone || contributor?.phoneNumber || '');
    if (contributorPhotoUri && contributorId) {
      handleCopyPhoto(contributorPhotoUri);
    }
  };

  const handleCopyField = (
    field: 'fullName' | 'title' | 'organization' | 'biography' | 'email' | 'phoneNumber' | 'assetName',
  ) => {
    if (!contributorProfile) return;
    switch (field) {
      case 'fullName':
        setValue('general.fullName', contributorProfile.displayName || '');
        break;
      case 'title':
        setValue('general.title', contributorProfile.title || '');
        break;
      case 'organization':
        setValue('general.organization', contributorProfile.organization || '');
        break;
      case 'biography':
        setValue('general.biography', contributorProfile.bio || '');
        break;
      case 'email':
        setValue('general.email', contributorProfile.contactEmail || contributor?.email || '');
        break;
      case 'phoneNumber':
        setValue('general.phoneNumber', contributorProfile.contactPhone || contributor?.phoneNumber || '');
        break;
      case 'assetName':
        if (contributorPhotoUri && contributorId) {
          handleCopyPhoto(contributorPhotoUri);
        }
        break;
    }
  };

  const handleCopyPhoto = async (sourcePhotoUri: string) => {
    if (!contributorId) return;

    setIsUploadingPhoto(true);
    try {
      const response = await fetch(sourcePhotoUri);
      if (!response.ok) {
        throw new Error('Failed to download photo');
      }
      const blob = await response.blob();

      const fileName = sourcePhotoUri.split('/').pop()?.split('?')[0] || 'photo.jpg';
      const fileExtension = fileName.split('.').pop() || 'jpg';
      const mimeType = blob.type || `image/${fileExtension}`;
      const file = new File([blob], fileName, { type: mimeType });

      const result = await uploadProfilePhotoMutation({
        contributorId,
        file,
      });

      if (result?.assetName) {
        setUploadedAssetName(result.assetName);
        setValue('general.assetName', result.assetName);
        if (contributorId && result.assetName) {
          await queryClient.invalidateQueries({
            queryKey: [GET_CONTRIBUTOR_PROFILE_PHOTO_URL_QUERY_KEY, contributorId, result.assetName],
          });
          await queryClient.refetchQueries({
            queryKey: [GET_CONTRIBUTOR_PROFILE_PHOTO_URL_QUERY_KEY, contributorId, result.assetName],
          });
        }
        if (eventId) {
          await queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY, eventId] });
          await queryClient.refetchQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY, eventId] });
        }
      }
      notification.notifySuccess(
        t('user.notification.profilePhotoUploadSuccess') || 'Profile photo copied successfully',
      );
    } catch {
      notification.notifyError(t('user.notification.profilePhotoUploadError') || 'Failed to copy profile photo');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handlePhotoUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !contributorId) return;

    setIsUploadingPhoto(true);
    try {
      const result = await uploadProfilePhotoMutation({
        contributorId,
        file,
      });
      if (result?.assetName) {
        setUploadedAssetName(result.assetName);
        setValue('general.assetName', result.assetName);
        if (eventId) {
          await queryClient.invalidateQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY, eventId] });
          await queryClient.refetchQueries({ queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY, eventId] });
        }
        if (contributorId) {
          queryClient.invalidateQueries({
            queryKey: [GET_CONTRIBUTOR_PROFILE_PHOTO_URL_QUERY_KEY, contributorId],
          });
        }
      }
      notification.notifySuccess(
        t('user.notification.profilePhotoUploadSuccess') || 'Profile photo uploaded successfully',
      );
      if (event.target) {
        event.target.value = '';
      }
    } catch {
      notification.notifyError(t('user.notification.profilePhotoUploadError') || 'Failed to upload profile photo');
      if (event.target) {
        event.target.value = '';
      }
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const fields = [
    {
      key: 'fullName' as const,
      label: t('event.detail.addEditContributor.steps.general.values.fullName'),
      contributorValue: contributorProfile?.displayName || '',
      multiline: false,
    },
    {
      key: 'title' as const,
      label: t('event.detail.contributors.titleInputLabel'),
      contributorValue: contributorProfile?.title || '',
      multiline: false,
    },
    {
      key: 'organization' as const,
      label: t('event.detail.addEditContributor.steps.general.values.organization'),
      contributorValue: contributorProfile?.organization || '',
      multiline: false,
    },
    {
      key: 'biography' as const,
      label: t('event.detail.addEditContributor.steps.biography.values.biography'),
      contributorValue: contributorProfile?.bio || '',
      multiline: true,
      rows: 5,
    },
    {
      key: 'email' as const,
      label: t('event.detail.addEditContributor.steps.general.values.email'),
      contributorValue: contributorProfile?.contactEmail || contributor?.email || '',
      multiline: false,
      type: 'email' as const,
    },
    {
      key: 'phoneNumber' as const,
      label: t('event.detail.addEditContributor.steps.general.values.phoneNumber'),
      contributorValue: contributorProfile?.contactPhone || contributor?.phoneNumber || '',
      multiline: false,
      type: 'tel' as const,
    },
  ];

  const watchedValues = useWatch({ control, name: 'general' });

  const fieldDiffs = useMemo(() => {
    if (!hasContributorProfile) return {};
    const diffs: Record<string, boolean> = {};
    for (const field of fields) {
      const contributorVal = (field.contributorValue || '').trim();
      const eventVal = ((watchedValues as Record<string, string | undefined>)?.[field.key] || '').trim();
      diffs[field.key] = contributorVal !== eventVal && contributorVal !== '';
    }
    return diffs;
  }, [fields, watchedValues, hasContributorProfile]);

  const panelSx = {
    flex: 1,
    minWidth: 0,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'background.paper',
    overflow: 'hidden',
  };

  const panelHeaderSx = {
    px: 2,
    py: 1.5,
    borderBottom: '1px solid',
    borderColor: 'divider',
    bgcolor: 'action.hover',
  };

  return (
    <Box sx={{ height: '100%', overflow: 'auto', width: '100%', boxSizing: 'border-box', p: { xs: 1, sm: 2 } }}>
      {showComparisonView ? (
        <Stack spacing={2} sx={{ width: '100%' }}>
          {/* Review status banner */}
          {hasContributorProfile && profileReviewStatus === 'warning' && (
            <Alert
              severity="warning"
              variant="outlined"
              action={
                <Button
                  color="warning"
                  size="small"
                  variant="contained"
                  onClick={handleMarkAsReviewed}
                  disabled={isMarkingReviewed}
                  startIcon={
                    isMarkingReviewed ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <CheckCircleOutlineIcon fontSize="small" />
                    )
                  }
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  {t('event.detail.addEditContributor.markAsReviewed')}
                </Button>
              }
              sx={{ alignItems: 'center' }}
            >
              {t('event.detail.addEditContributor.reviewPending')}
            </Alert>
          )}

          {hasContributorProfile && profileReviewStatus === 'success' && (
            <Alert
              severity="success"
              variant="outlined"
              icon={<CheckCircleOutlineIcon fontSize="small" />}
              sx={{ py: 0.5 }}
            >
              {t('event.detail.addEditContributor.profileReviewed')}
            </Alert>
          )}

          {/* View mode toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => v && setViewMode(v)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  'px': { xs: 1, sm: 2 },
                  'py': 0.5,
                  'textTransform': 'none',
                  'fontSize': { xs: '0.75rem', sm: '0.8125rem' },
                  'borderColor': 'divider',
                  '&.Mui-selected': {
                    'backgroundColor': 'secondary.main',
                    'color': 'secondary.contrastText',
                    '&:hover': { backgroundColor: 'secondary.dark' },
                  },
                },
              }}
            >
              <ToggleButton value="contributor">
                {t('event.detail.addEditContributor.viewMode.contributorOnly')}
              </ToggleButton>
              <ToggleButton value="comparison">{t('event.detail.addEditContributor.viewMode.comparison')}</ToggleButton>
              <ToggleButton value="event">{t('event.detail.addEditContributor.viewMode.eventOnly')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Panels */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: viewMode === 'comparison' ? { xs: 'column', sm: 'row' } : 'column',
            }}
          >
            {/* Contributor Input panel */}
            {showContributorPanel && (
              <Box sx={{ ...panelSx, opacity: hasContributorProfile ? 1 : 0.5, width: '100%' }}>
                <Box sx={panelHeaderSx}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {t('event.detail.addEditContributor.contributorInput')}
                    </Typography>
                    {viewMode === 'comparison' && (
                      <Tooltip title={t('common.copyAll')}>
                        <span>
                          <Button
                            size="small"
                            onClick={handleCopyAll}
                            disabled={!hasContributorProfile}
                            startIcon={<ContentCopyIcon sx={{ fontSize: '0.875rem' }} />}
                            endIcon={<EastIcon sx={{ fontSize: '0.875rem' }} />}
                            sx={{
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              minWidth: 0,
                              px: 1,
                            }}
                          >
                            {t('common.copyAll')}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>

                <Box sx={{ p: 2 }}>
                  {/* Contributor photo */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box sx={{ position: 'relative' }}>
                      <ProfilePhotoAvatar
                        displayName={contributorProfile?.displayName}
                        src={contributorPhotoUri || undefined}
                        onClick={() => contributorPhotoUri && setIsContributorPhotoPreviewOpen(true)}
                        sx={{
                          'width': 80,
                          'height': 100,
                          'bgcolor': 'primary.main',
                          'border': '1px solid',
                          'borderColor': 'divider',
                          'borderRadius': 1.5,
                          'cursor': contributorPhotoUri ? 'pointer' : 'default',
                          '& img': { objectFit: 'cover' },
                        }}
                      />
                      {viewMode === 'comparison' && contributorPhotoUri && hasContributorProfile && (
                        <IconButton
                          size="small"
                          onClick={() => handleCopyField('assetName')}
                          sx={{
                            'position': 'absolute',
                            'bottom': -6,
                            'right': -6,
                            'bgcolor': 'background.paper',
                            'border': '1px solid',
                            'borderColor': 'divider',
                            'width': 26,
                            'height': 26,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <EastIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {!hasContributorProfile && (
                    <Typography
                      variant="body2"
                      sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', py: 2 }}
                    >
                      {t('event.detail.addEditContributor.noContributorProfile')}
                    </Typography>
                  )}

                  {/* Contributor fields (read-only) */}
                  <Stack spacing={1.5}>
                    {fields.map((field) => {
                      const isDiff = !!fieldDiffs[field.key];
                      const eventVal = (
                        (watchedValues as Record<string, string | undefined>)?.[field.key] || ''
                      ).trim();
                      return (
                        <Box
                          key={field.key}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.5,
                            borderLeft: isDiff ? '3px solid' : '3px solid transparent',
                            borderColor: isDiff ? 'warning.main' : 'transparent',
                            pl: isDiff ? 1 : 0,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {isDiff && field.contributorValue && eventVal ? (
                              <Box sx={{ py: 0.5 }}>
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'text.secondary', mb: 0.25, display: 'block' }}
                                >
                                  {field.label}
                                </Typography>
                                <DiffText oldStr={eventVal} newStr={field.contributorValue} />
                              </Box>
                            ) : (
                              <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                label={field.label}
                                value={field.contributorValue}
                                disabled
                                multiline={field.multiline}
                                minRows={field.multiline ? field.rows : undefined}
                                type={field.type}
                                sx={{
                                  '& .MuiOutlinedInput-root': { fontSize: '0.875rem' },
                                  '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                                }}
                              />
                            )}
                          </Box>
                          {viewMode === 'comparison' && (
                            <Tooltip title={field.label}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCopyField(field.key)}
                                  disabled={!hasContributorProfile || !field.contributorValue}
                                  sx={{ mt: 0.5 }}
                                >
                                  <EastIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
            )}

            {/* Event Profile panel (editable) */}
            {showEventPanel && (
              <Box sx={{ ...panelSx, width: '100%' }}>
                <Box sx={panelHeaderSx}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t('myEventsList.contributorProfileForm.storedInEvent')}
                  </Typography>
                </Box>

                <Box sx={{ p: 2 }}>
                  {/* Event photo with upload */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box sx={{ position: 'relative' }}>
                      <ProfilePhotoAvatar
                        contributorId={contributorId}
                        assetName={eventAssetName}
                        displayName={eventProfile?.displayName}
                        onClick={() => eventAssetName && setIsEventPhotoPreviewOpen(true)}
                        sx={{
                          'width': 80,
                          'height': 100,
                          'bgcolor': 'primary.main',
                          'border': '1px solid',
                          'borderColor': 'divider',
                          'borderRadius': 1.5,
                          'cursor': eventAssetName ? 'pointer' : 'default',
                          '& img': { objectFit: 'cover' },
                        }}
                      />
                      {isUploadingPhoto ? (
                        <CircularProgress
                          size={18}
                          sx={{ position: 'absolute', bottom: 2, right: 2, color: 'primary.main' }}
                        />
                      ) : (
                        <IconButton
                          size="small"
                          sx={{
                            'position': 'absolute',
                            'bottom': -6,
                            'right': -6,
                            'bgcolor': 'background.paper',
                            'border': '1px solid',
                            'borderColor': 'divider',
                            'width': 26,
                            'height': 26,
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handlePhotoUpload}
                        accept="image/*"
                        style={{ display: 'none' }}
                      />
                    </Box>
                  </Box>

                  {/* Event fields (editable) */}
                  <Stack spacing={1.5}>
                    {fields.map((field) => (
                      <Controller
                        key={field.key}
                        name={`general.${field.key}` as Path<StepsState>}
                        control={control}
                        render={({ field: formField, fieldState }) => (
                          <TextField
                            {...formField}
                            value={formField.value ?? ''}
                            fullWidth
                            variant="outlined"
                            size="small"
                            label={field.label}
                            helperText={fieldState.error ? fieldState.error.message : null}
                            error={!!fieldState.error}
                            multiline={field.multiline}
                            minRows={field.multiline ? field.rows : undefined}
                            type={field.type}
                            sx={{
                              '& .MuiOutlinedInput-root': { fontSize: '0.875rem' },
                              '& .MuiInputLabel-root': { fontSize: '0.875rem' },
                            }}
                          />
                        )}
                      />
                    ))}
                  </Stack>
                </Box>
              </Box>
            )}
          </Box>
        </Stack>
      ) : (
        /* Single column view when creating new contributor */
        <Stack spacing={2} sx={{ width: '100%', maxWidth: 480, mx: 'auto' }}>
          {/* Avatar with upload */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
            <Box sx={{ position: 'relative' }}>
              <ProfilePhotoAvatar
                contributorId={contributorId}
                assetName={eventAssetName}
                displayName={eventProfile?.displayName}
                onClick={() => eventAssetName && setIsEventPhotoPreviewOpen(true)}
                sx={{
                  'width': 80,
                  'height': 100,
                  'bgcolor': 'primary.main',
                  'border': '1px solid',
                  'borderColor': 'divider',
                  'borderRadius': 1.5,
                  'cursor': eventAssetName ? 'pointer' : 'default',
                  '& img': { objectFit: 'cover' },
                }}
              />
              {isUploadingPhoto ? (
                <CircularProgress size={18} sx={{ position: 'absolute', bottom: 2, right: 2, color: 'primary.main' }} />
              ) : (
                <IconButton
                  size="small"
                  sx={{
                    'position': 'absolute',
                    'bottom': -6,
                    'right': -6,
                    'bgcolor': 'background.paper',
                    'border': '1px solid',
                    'borderColor': 'divider',
                    'width': 26,
                    'height': 26,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </Box>
          </Box>

          {/* Editable fields */}
          {fields.map((field) => (
            <Controller
              key={field.key}
              name={`general.${field.key}` as Path<StepsState>}
              control={control}
              render={({ field: formField, fieldState }) => (
                <TextField
                  {...formField}
                  value={formField.value ?? ''}
                  fullWidth
                  variant="outlined"
                  size="small"
                  label={field.label}
                  helperText={fieldState.error ? fieldState.error.message : null}
                  error={!!fieldState.error}
                  multiline={field.multiline}
                  minRows={field.multiline ? field.rows : undefined}
                  type={field.type}
                />
              )}
            />
          ))}
        </Stack>
      )}

      {/* Photo Preview Modals */}
      <ProfileImagePreview
        open={isContributorPhotoPreviewOpen}
        onClose={() => setIsContributorPhotoPreviewOpen(false)}
        imageUrl={contributorPhotoUri}
        alt={contributorProfile?.displayName || 'Profile'}
      />

      <ProfileImagePreview
        open={isEventPhotoPreviewOpen}
        onClose={() => setIsEventPhotoPreviewOpen(false)}
        imageUrl={eventPhotoUrl || undefined}
        alt={eventProfile?.displayName || 'Profile'}
      />
    </Box>
  );
}
